import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Receipt, Plus } from "lucide-react";

export default function Dues() {
  const { user } = useAuth();
  const isBoard = user?.role === "admin" || user?.role === "board";
  const [data, setData] = useState({ entries: [], balances: {} });
  const [owners, setOwners] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ owner_id: "", amount: "", kind: "charge", description: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/dues");
    setData(data);
    if (isBoard) {
      const { data: users } = await api.get("/users");
      setOwners(users.filter((u) => u.role === "owner"));
    }
  }, [isBoard]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/dues", { ...form, amount: parseFloat(form.amount) });
      toast.success("Recorded");
      setForm({ owner_id: "", amount: "", kind: "charge", description: "" });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not record");
    } finally {
      setSaving(false);
    }
  };

  const myBalance = data.balances?.[user?.id] ?? 0;

  return (
    <div className="space-y-6" data-testid="dues-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-secondary text-sm font-medium mb-2">
            <Receipt className="w-4 h-4" /> Financials
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">Dues & assessments</h1>
        </div>
        {isBoard && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full h-11" data-testid="new-dues-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Record entry
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Record a dues entry</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="dues-form">
                <div>
                  <Label>Owner</Label>
                  <Select value={form.owner_id} onValueChange={(v) => setForm({ ...form, owner_id: v })}>
                    <SelectTrigger className="mt-1.5 h-11" data-testid="dues-owner-select"><SelectValue placeholder="Select owner" /></SelectTrigger>
                    <SelectContent>
                      {owners.map((o) => (
                        <SelectItem key={o.id} value={o.id}>{o.name}{o.unit_number ? ` · ${o.unit_number}` : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Type</Label>
                    <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                      <SelectTrigger className="mt-1.5 h-11" data-testid="dues-kind-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="charge">Charge (billed)</SelectItem>
                        <SelectItem value="payment">Payment received</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Amount ($)</Label>
                    <Input required type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5 h-11" data-testid="dues-amount-input" />
                  </div>
                </div>
                <div>
                  <Label>Description</Label>
                  <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 h-11" placeholder="Monthly dues - Jan 2026" data-testid="dues-desc-input" />
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={saving || !form.owner_id} className="rounded-full" data-testid="dues-submit-btn">
                    {saving ? "Saving..." : "Save entry"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {!isBoard && (
        <div className="hoa-card p-8" data-testid="my-balance-card">
          <div className="text-sm text-muted-foreground">Your current balance</div>
          <div className={`text-5xl font-semibold tracking-tight font-mono mt-2 ${myBalance > 0 ? "text-destructive" : "text-primary"}`}>
            ${myBalance.toFixed(2)}
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            {myBalance > 0 ? "Amount owed to the HOA." : myBalance < 0 ? "Credit on file." : "You're all paid up. Thank you!"}
          </p>
        </div>
      )}

      {isBoard && (
        <div className="hoa-card p-6" data-testid="board-balances-card">
          <h3 className="font-semibold text-lg mb-4 text-primary">Balance by owner</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {owners.map((o) => {
              const b = data.balances?.[o.id] ?? 0;
              return (
                <div key={o.id} className="flex items-center justify-between p-3 rounded-lg bg-accent/40" data-testid={`bal-${o.id}`}>
                  <div>
                    <div className="text-sm font-medium">{o.name}</div>
                    <div className="text-xs text-muted-foreground">{o.unit_number || "—"}</div>
                  </div>
                  <div className={`font-mono font-semibold ${b > 0 ? "text-destructive" : "text-primary"}`}>
                    ${b.toFixed(2)}
                  </div>
                </div>
              );
            })}
            {owners.length === 0 && <p className="text-sm text-muted-foreground">No owners yet.</p>}
          </div>
        </div>
      )}

      <div>
        <h3 className="font-semibold text-lg text-primary mb-3">Ledger</h3>
        <div className="hoa-card overflow-hidden">
          {data.entries.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground" data-testid="dues-empty">No entries yet.</div>
          ) : (
            <div className="divide-y divide-border">
              {data.entries.map((e) => (
                <div key={e.id} className="p-4 flex items-center justify-between" data-testid={`dues-entry-${e.id}`}>
                  <div>
                    <div className="text-sm font-medium">
                      {e.description || (e.kind === "charge" ? "Charge" : "Payment")}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {isBoard ? `${e.owner_name}${e.owner_unit ? ` (${e.owner_unit})` : ""} · ` : ""}
                      {new Date(e.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className={`font-mono font-semibold ${e.kind === "charge" ? "text-destructive" : "text-primary"}`}>
                    {e.kind === "charge" ? "+" : "-"}${e.amount.toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
